import { authFetch } from "../../lib/authFetch";
import {
  Agency,
  Carrier,
  DeliveredType,
  OrderDetail,
  OrderPerson,
  OrderSummary,
  QuoteHistoryItem,
  QuoteMode,
  RateResult,
} from "./types";

// Misma clave que usa AuthContext para guardar la sesión.
const AUTH_TOKEN_KEY = "auth_token";

function getAuthToken(): string | null {
  try {
    return localStorage.getItem(AUTH_TOKEN_KEY);
  } catch (error) {
    console.error("No se pudo leer auth_token desde localStorage", error);
    return null;
  }
}

export type QuoteParams = {
  postalCodeOrigin: string;
  postalCodeDestination: string;
  deliveredType: DeliveredType;
  weight: number;
  height: number;
  width: number;
  length: number;
  mode?: QuoteMode;
  label?: string;
};

export type QuoteResponse = {
  id: string | null;
  createdAt: string;
  validTo: string | null;
  rates: RateResult[];
  // Correo responde 200 con la lista vacía cuando no ofrece servicio para ese
  // bulto: no es un error, pero tampoco es una cotización.
  sinTarifas: boolean;
  volumeCm3?: number;
  aforoKg?: number;
  billableKg?: number;
  customerId?: string;
  carrierErrors?: Partial<Record<Carrier, string>>;
};

export type QuoteHistoryPage = {
  total: number;
  quotes: QuoteHistoryItem[];
};

// Cotiza contra nuestro backend, que es quien habla con MiCorreo.
// Las credenciales de Correo viven en el servidor: el navegador no las conoce.
export async function quoteRates({
  postalCodeOrigin,
  postalCodeDestination,
  deliveredType,
  weight,
  height,
  width,
  length,
  mode,
  label,
}: QuoteParams): Promise<QuoteResponse> {
  const response = await authFetch("/v2/lynxpack/quotes", {
    method: "POST",
    json: true,
    token: getAuthToken(),
    body: JSON.stringify({
      postalCodeOrigin,
      postalCodeDestination,
      deliveredType,
      mode,
      label,
      dimensions: { weight, height, width, length },
    }),
  });

  let data: any = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok || !data?.ok) {
    throw new Error(
      data?.error || `No se pudo cotizar (HTTP ${response.status})`
    );
  }

  return {
    id: data.id ?? null,
    createdAt: data.createdAt ?? new Date().toISOString(),
    validTo: data.validTo ?? null,
    rates: Array.isArray(data.rates) ? data.rates : [],
    sinTarifas: Boolean(data.sinTarifas),
    volumeCm3: data.volumeCm3,
    aforoKg: data.aforoKg,
    billableKg: data.billableKg,
    customerId: data.customerId,
    carrierErrors: data.carrierErrors ?? undefined,
  };
}

// Trae el historial guardado. Antes vivía en memoria y se perdía al recargar;
// ahora es lo que respalda el precio que se le cotizó a un cliente.
export async function fetchQuoteHistory({
  limit = 50,
  offset = 0,
}: { limit?: number; offset?: number } = {}): Promise<QuoteHistoryPage> {
  const response = await authFetch(
    `/v2/lynxpack/quotes?limit=${limit}&offset=${offset}`,
    { token: getAuthToken() }
  );

  let data: any = null;

  try {
    data = await response.json();
  } catch {
    data = null;
  }

  if (!response.ok || !data?.ok) {
    throw new Error(
      data?.error || `No se pudo leer el historial (HTTP ${response.status})`
    );
  }

  return {
    total: Number(data.total) || 0,
    quotes: Array.isArray(data.quotes) ? data.quotes : [],
  };
}

/* =========================
   Órdenes, cobro y despacho (fases 3-6)
   ========================= */

async function parseJson(response: Response) {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

function throwIfError(response: Response, data: any, fallback: string) {
  if (!response.ok || !data?.ok) {
    const error = new Error(data?.error || `${fallback} (HTTP ${response.status})`);
    (error as any).codigo = data?.codigo;
    (error as any).status = response.status;
    throw error;
  }
}

// `crypto.randomUUID()` solo existe en contexto seguro (https, o localhost):
// servida por http desde una IP, la llamada tira TypeError y crear una orden
// fallaba con un mensaje que no explicaba nada. La clave solo tiene que ser
// única por pedido, así que el respaldo alcanza de sobra.
function claveDeIdempotencia(): string {
  const cripto = globalThis.crypto as Crypto | undefined;

  if (typeof cripto?.randomUUID === "function") return cripto.randomUUID();

  const azar =
    typeof cripto?.getRandomValues === "function"
      ? Array.from(cripto.getRandomValues(new Uint8Array(16)))
          .map((b) => b.toString(16).padStart(2, "0"))
          .join("")
      : Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

  return `lp-${Date.now().toString(36)}-${azar}`;
}

export type CreateOrderParams = {
  quoteId: number;
  carrier: Carrier;
  productType: string;
  deliveredType: "D" | "S";
  declaredValue: number;
  recipient: OrderPerson;
  agencyId?: string;
};

// El alta exige una clave de idempotencia: un doble clic o un reintento de
// red devuelven la misma orden en vez de crear dos.
export async function createOrder(params: CreateOrderParams): Promise<OrderDetail> {
  const response = await authFetch("/v2/lynxpack/orders", {
    method: "POST",
    json: true,
    token: getAuthToken(),
    headers: { "Idempotency-Key": claveDeIdempotencia() },
    body: JSON.stringify(params),
  });

  const data = await parseJson(response);
  throwIfError(response, data, "No se pudo crear la orden");

  return data.order as OrderDetail;
}

export async function fetchOrder(orderId: string): Promise<OrderDetail> {
  const response = await authFetch(`/v2/lynxpack/orders/${orderId}`, {
    token: getAuthToken(),
  });

  const data = await parseJson(response);
  throwIfError(response, data, "No se pudo leer la orden");

  return data.order as OrderDetail;
}

export async function fetchOrders({
  limit = 25,
  offset = 0,
}: { limit?: number; offset?: number } = {}): Promise<{ total: number; orders: OrderSummary[] }> {
  const response = await authFetch(
    `/v2/lynxpack/orders?limit=${limit}&offset=${offset}`,
    { token: getAuthToken() }
  );

  const data = await parseJson(response);
  throwIfError(response, data, "No se pudieron listar las órdenes");

  return {
    total: Number(data.total) || 0,
    orders: Array.isArray(data.orders) ? data.orders : [],
  };
}

export type PayOrderResult = {
  initPoint: string;
  simulado: boolean;
  order: OrderDetail;
};

// Abre el cobro (o devuelve el mismo link si ya estaba abierto). Si la
// cotización tiene más de 30 minutos, el servidor contesta 409 con
// codigo="cotizacion_vencida": hay que cotizar de nuevo.
export async function payOrder(orderId: string): Promise<PayOrderResult> {
  const response = await authFetch(`/v2/lynxpack/orders/${orderId}/pagar`, {
    method: "POST",
    json: true,
    token: getAuthToken(),
  });

  const data = await parseJson(response);
  throwIfError(response, data, "No se pudo abrir el cobro");

  return {
    initPoint: data.initPoint,
    simulado: Boolean(data.simulado),
    order: data.order as OrderDetail,
  };
}

export type SimulatedOutcome = "aprobado" | "rechazado" | "pendiente";

// Solo existe sin credenciales reales de Mercado Pago: decide el desenlace
// del pago como si el comprador hubiera terminado el checkout.
export async function simulatePayment(
  orderId: string,
  resultado: SimulatedOutcome
): Promise<OrderDetail> {
  const response = await authFetch(`/v2/lynxpack/orders/${orderId}/simular-pago`, {
    method: "POST",
    json: true,
    token: getAuthToken(),
    body: JSON.stringify({ resultado }),
  });

  const data = await parseJson(response);

  // Un doble aprobado o un conflicto contestan ok=false pero con la orden:
  // se muestra el estado real, no un error.
  if (!response.ok || (!data?.ok && !data?.order)) {
    throwIfError(response, data, "No se pudo simular el pago");
  }

  return data.order as OrderDetail;
}

export async function dispatchOrder(orderId: string): Promise<{ ok: boolean; error?: string; order: OrderDetail }> {
  const response = await authFetch(`/v2/lynxpack/orders/${orderId}/despachar`, {
    method: "POST",
    json: true,
    token: getAuthToken(),
  });

  const data = await parseJson(response);

  // 502 = el despacho falló y la orden quedó en envio_fallido: no es una
  // excepción, es un estado que la pantalla muestra con su motivo.
  if (response.status === 502 && data?.order) {
    return { ok: false, error: data.error, order: data.order as OrderDetail };
  }

  throwIfError(response, data, "No se pudo despachar la orden");

  return { ok: true, order: data.order as OrderDetail };
}

// El rótulo llega como PDF con autenticación: se baja como blob y se abre en
// una pestaña (un <a href> directo no llevaría el token).
export async function openLabel(orderId: string): Promise<void> {
  const response = await authFetch(`/v2/lynxpack/orders/${orderId}/etiqueta`, {
    token: getAuthToken(),
  });

  if (!response.ok) {
    const data = await parseJson(response);
    throw new Error(data?.error || `No se pudo obtener el rótulo (HTTP ${response.status})`);
  }

  const blob = await response.blob();
  const url = URL.createObjectURL(blob);

  window.open(url, "_blank", "noopener");
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// `postalCode` es el CP de la cotización: el backend devuelve solo las
// sucursales de ese código postal, que son las únicas con las que el alta de
// la orden va a aceptar el precio cotizado.
export async function fetchAgencies(
  stateId?: string,
  postalCode?: string,
  carrier: Carrier = "correo"
): Promise<Agency[]> {
  const parametros = new URLSearchParams();

  if (stateId) parametros.set("stateId", stateId);
  if (postalCode) parametros.set("postalCode", postalCode);
  if (carrier !== "correo") parametros.set("carrier", carrier);

  const sufijo = parametros.toString() ? `?${parametros.toString()}` : "";
  const response = await authFetch(`/v2/lynxpack/agencies${sufijo}`, {
    token: getAuthToken(),
  });

  const data = await parseJson(response);
  throwIfError(response, data, "No se pudieron consultar las sucursales");

  return Array.isArray(data.agencies) ? data.agencies : [];
}
